import React, {useContext, useEffect, useRef, useState, useCallback} from "react";
import {useSearchParams} from "react-router-dom";
import StyleContext from "../../contexts/StyleContext";
import {fetchResume, trackResumeEvent} from "../../services/api";
import {useLoading} from "../../contexts/LoadingContext";
import Button from "../../components/button/Button";
import Loader from "../../components/loader/Loader";
import "./Resume.scss";

// Render bullets / strings safely
const Bullets = ({items = []}) => (
  <ul className="rd-resume__bullets">
    {items.map((b, i) => <li key={i}>{b}</li>)}
  </ul>
);

const Resume = () => {
  const {isDark}      = useContext(StyleContext);
  const {show: showOverlay, hide: hideOverlay} = useLoading();
  const [data, setData]       = useState(null);
  const [pdfEnabled, setPdf]  = useState(true);
  const [loading, setLoading] = useState(true);
  const trackedRef = useRef(false);
  const autoDownloadFiredRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    showOverlay("Loading resume");
    try {
      const res = await fetchResume();   // never throws — handled inside
      setData(res?.resume || null);
      setPdf(res?.pdfEnabled !== false);
    } finally {
      setLoading(false);
      hideOverlay();
    }
  }, [showOverlay, hideOverlay]);

  useEffect(() => { load(); }, [load]);

  // Fire one view event per page-mount (after data shows).
  useEffect(() => {
    if (!loading && data && !trackedRef.current) {
      trackedRef.current = true;
      trackResumeEvent("view");
    }
  }, [loading, data]);

  // ----- Auto-download when arrived via "?download=1" -----
  // Greeting/Portfolio's "Download my resume" Button links to /resume?download=1.
  // We wait until the resume content is rendered, then fire the same print flow
  // the on-page Download button uses. Then we strip the query param so a refresh
  // doesn't keep re-printing.
  useEffect(() => {
    if (autoDownloadFiredRef.current) return;
    if (loading || !data || !pdfEnabled) return;
    if (searchParams.get("download") !== "1") return;

    autoDownloadFiredRef.current = true;
    // tiny delay so React has fully painted the DOM before print snapshots it
    const t = setTimeout(() => {
      downloadPdf();
      // Remove ?download=1 from the address bar.
      const next = new URLSearchParams(searchParams);
      next.delete("download");
      setSearchParams(next, {replace: true});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, pdfEnabled, searchParams]);

  // ----- Download as PDF — TRUE text-based PDF via window.print() -----
  // Why we ditched html2pdf.js:
  //   - it canvases the DOM and embeds a JPEG → text is rasterised, ATS bots
  //     can't read it, can't be copy/pasted, looks pixelated when zoomed.
  // Browser's built-in "Save as PDF" path:
  //   - Chrome/Edge: Save as PDF (selectable text, vector, ATS-friendly).
  //   - Firefox/Safari: Save as PDF / Export as PDF — same result.
  //   - Our @media print A4 stylesheet (Resume.scss) sizes the page at A4,
  //     hides toolbars/nav/footer, and prevents bad page breaks.
  const downloadPdf = () => {
    trackResumeEvent("download");
    // Tell the browser to invoke the print dialog. User picks
    // "Save as PDF" (it's the default destination on Chrome/Edge).
    if (typeof window === "undefined") return;

    // Brief title swap so the saved filename defaults to "Rashid_Ahmad_Resume".
    const originalTitle = document.title;
    document.title = "Rashid_Ahmad_Resume";
    document.body.classList.add("rd-print-mode");

    const restore = () => {
      document.title = originalTitle;
      document.body.classList.remove("rd-print-mode");
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    // Safety fallback (Safari sometimes skips afterprint)
    setTimeout(restore, 1500);

    window.print();
  };

  if (loading) return <Loader inline label="Loading resume" />;

  if (!data) {
    return (
      <div className="rd-resume__page">
        <p>Resume is temporarily unavailable. Please check back shortly.</p>
      </div>
    );
  }

  const h = data.header || {};

  return (
    <div className={`rd-resume__page ${isDark ? "dark" : ""}`}>
      <div className="rd-resume__toolbar no-print">
        <div>
          <h2 className="rd-resume__title-h2">My Resume</h2>
          <p className="rd-resume__sub">
            A full, formatted version of my CV — feel free to download.
          </p>
        </div>
        {pdfEnabled && (
          // SAME <Button> visual as Greeting (Portfolio) for parity.
          // onClick triggers the live-print PDF — there is no static file
          // and no external URL. Content comes from the admin-edited row in
          // `resume_content` (DB) via /resume.php → fetchResume().
          <div
            className="download-link-button rd-resume__dl-wrap"
            onClick={downloadPdf}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                downloadPdf();
              }
            }}
            aria-label="Download my resume as PDF"
          >
            <Button text="Download my resume" />
          </div>
        )}
      </div>

      {/* The actual A4 document */}
      <article className="rd-resume__doc" aria-label="Resume document">
        {/* HEADER */}
        <header className="rd-resume__header">
          <h1>{h.name || "Rashid Ahmad"}</h1>
          {h.title && <p className="rd-resume__role">{h.title}</p>}
          <p className="rd-resume__contact">
            {h.location && <span>{h.location}</span>}
            {h.email && <span><a href={`mailto:${h.email}`}>{h.email}</a></span>}
            {h.phone && <span><a href={`tel:${h.phone}`}>{h.phone}</a></span>}
            {h.linkedin && (
              <span>
                <a href={h.linkedin} target="_blank" rel="noopener noreferrer">
                  {h.linkedinLabel || h.linkedin}
                </a>
              </span>
            )}
          </p>
        </header>

        <hr className="rd-resume__rule" />

        {/* SUMMARY */}
        {data.summary && (
          <section className="rd-resume__section">
            <h2>Professional Summary</h2>
            <p>{data.summary}</p>
          </section>
        )}

        {/* EXPERIENCE */}
        {Array.isArray(data.experience) && data.experience.length > 0 && (
          <section className="rd-resume__section">
            <h2>Work Experience</h2>
            {data.experience.map((x, i) => (
              <div key={i} className="rd-resume__entry">
                <div className="rd-resume__entry-head">
                  <strong>{x.company}</strong>
                  <span className="rd-resume__when">{x.from} – {x.to}</span>
                </div>
                <div className="rd-resume__entry-sub">
                  <em>{x.role}</em>
                  {x.location && <span className="rd-resume__loc">{x.location}</span>}
                </div>
                {Array.isArray(x.bullets) && <Bullets items={x.bullets} />}
              </div>
            ))}
          </section>
        )}

        {/* KEY PROJECTS */}
        {Array.isArray(data.projects) && data.projects.length > 0 && (
          <section className="rd-resume__section">
            <h2>Key Projects</h2>
            {data.projects.map((p, i) => (
              <div key={i} className="rd-resume__entry">
                <div className="rd-resume__entry-head">
                  <strong>{p.name}</strong>
                  {p.period && <span className="rd-resume__when">{p.period}</span>}
                </div>
                {Array.isArray(p.bullets) && <Bullets items={p.bullets} />}
              </div>
            ))}
          </section>
        )}

        {/* EDUCATION */}
        {Array.isArray(data.education) && data.education.length > 0 && (
          <section className="rd-resume__section">
            <h2>Education</h2>
            {data.education.map((e, i) => (
              <div key={i} className="rd-resume__entry">
                <div className="rd-resume__entry-head">
                  <strong>{e.school}</strong>
                  <span className="rd-resume__when">{e.from} – {e.to}</span>
                </div>
                <div className="rd-resume__entry-sub">
                  <em>{e.degree}</em>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* SKILLS */}
        {Array.isArray(data.skills) && data.skills.length > 0 && (
          <section className="rd-resume__section">
            <h2>Skills</h2>
            <div className="rd-resume__chips">
              {data.skills.map((s, i) => (
                <span key={i} className="rd-resume__chip">{s}</span>
              ))}
            </div>
          </section>
        )}

        {/* CERTIFICATIONS */}
        {Array.isArray(data.certifications) && data.certifications.length > 0 && (
          <section className="rd-resume__section">
            <h2>Certifications</h2>
            <ul className="rd-resume__certs">
              {data.certifications.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
};

export default Resume;
